import { ClassValue } from 'clsx';
import { useEffect } from 'react';
import WarningTriangleIcon from '@/assets/icons/warning-triangle.svg?react';
import { Button, Modal, Text } from '@/components/base';
import Icon, { IconName } from '@/components/base/Icon';
import { ErrorLogData } from '@/interfaces/logging';
import { toClassNames } from '@/utils';
import { getCurrentSessionInfo } from '@/utils/activitySession';
import { logger } from '@/utils/logger';

interface RevampErrorModalProps {
  open: boolean;
  title: string;
  description: string;
  buttonText: string;
  onButtonClick: () => void;
  icon?: IconName;
  iconClassName?: ClassValue;
  errorLogData?: ErrorLogData;
}

const RevampErrorModal = ({
  open,
  title,
  description,
  buttonText,
  onButtonClick,
  icon,
  iconClassName,
  errorLogData,
}: RevampErrorModalProps) => {
  const { location } = window;
  const pageView = location.pathname + location.search;

  const currentSession = getCurrentSessionInfo();

  useEffect(() => {
    if (open) {
      const {
        action,
        component = 'RevampErrorModal',
        description: errorDescription,
        context,
        error,
      } = errorLogData || {};

      const logPayload = {
        component: errorLogData?.component ?? 'RevampErrorModal',
        pageView,
        modalDescription: description,
        action,
        description: errorDescription,
        context,
        user: { id: currentSession?.userId },
        sessionId: currentSession?.sessionId,
        title,
      };
      const logMessage = `Error displayed in ${component}: ${title}`;

      if (error instanceof Error) {
        logger.error(logMessage, error, logPayload);
      } else {
        logger.error(logMessage, undefined, { ...logPayload, error });
      }
    }
  }, [open, pageView, errorLogData, currentSession?.userId, title, description, currentSession?.sessionId]);

  return (
    <Modal
      open={open}
      dialogClassname="z-[60]"
      // 24px gutter on narrow viewports, matching the design, instead of the default 16.
      containerClassnames="p-6"
      // `rounded-[20px]`, not `rounded-edsy-2xl` — tailwind-merge would keep Modal's `rounded-2xl` too.
      className="max-w-[345px] rounded-[20px] p-6 font-fustat"
    >
      <div className="flex flex-col items-center gap-6">
        {icon ? (
          <Icon icon={icon} className={toClassNames('text-revamp-yellow-500', iconClassName)} />
        ) : (
          <WarningTriangleIcon className={toClassNames('h-10 w-[45.702px]', iconClassName)} />
        )}

        <div className="flex flex-col items-center gap-2 text-center">
          <Text version="revamp-v1" variant="body3" className="font-bold text-revamp-gray-900">
            {title}
          </Text>
          <Text version="revamp-v1" variant="body5" className="font-semibold text-revamp-gray-500">
            {description}
          </Text>
        </div>

        <Button
          version="revamp-v1"
          variant="solid"
          color="primary"
          size="xl"
          className="w-full"
          onClick={onButtonClick}
        >
          {buttonText}
        </Button>
      </div>
    </Modal>
  );
};

export default RevampErrorModal;
